import { useMemo } from "react";
import { useSelector } from "react-redux";
import { FlightShopSelectors, getFlightData } from "@b2bportal/core-flights";
import { useModuleBEM } from "@b2bportal/core-themes";
import { FlightShopStep } from "@b2bportal/core-types";
import { useI18nContext } from "@hopper-b2b/i18n";
import { FlightCard } from "@tenant/flights";
import defaultStyles from "./PackageFlightCard.module.scss";
import { PackageBaseSelectors } from "@/store/packages/selectors";

export const PackageFlightCard = (
  props: React.ComponentProps<typeof FlightCard>
) => {
  const [, cn] = useModuleBEM(defaultStyles, "PackageFlightCard");

  const { t } = useI18nContext();

  // Find the benchmark flight
  const benchmarkFlight = useSelector(PackageBaseSelectors.getBenchmarkFlight);

  // Get the flight data from the slice
  const flightData = useSelector((state) =>
    getFlightData(state, props.flight.sliceId)
  );

  const shopStep = useSelector(FlightShopSelectors.getFlightShopStep);

  // This is hacky at the moment but it's really the best we can do until we have matching slice id's
  const isBaseFlight = useMemo(() => {
    // Compare the current flight with the benchmark flight
    let compareFlight = benchmarkFlight?.outbound;
    if (!compareFlight || !flightData?.segments?.[0]) {
      return false;
    }

    // Check if we're actually in a return flight
    if (shopStep === FlightShopStep.return) {
      compareFlight = benchmarkFlight?.returning;
    }
    const firstSegment = flightData.segments[0];

    // Calculate total duration in minutes from flightData.duration
    const totalDurationMinutes =
      flightData.duration.days * 24 * 60 +
      flightData.duration.hours * 60 +
      flightData.duration.minutes;

    const hasSameOrigin = compareFlight.origin === firstSegment.origin;
    const hasSameFlightNumber =
      compareFlight.flightNumber === firstSegment.flightNumber;
    const hasSameMarketingAirline =
      compareFlight.marketingAirline === firstSegment.marketingAirline;
    const hasSameOperatingAirline =
      compareFlight.operatingAirline === firstSegment.operatingAirline;
    const hasSameStops = compareFlight.stops === flightData.flightStops;
    const hasSameTotalDurationMinutes =
      compareFlight.totalDurationMinutes === totalDurationMinutes;

    return (
      hasSameOrigin &&
      hasSameFlightNumber &&
      hasSameMarketingAirline &&
      hasSameOperatingAirline &&
      hasSameStops &&
      hasSameTotalDurationMinutes
    );
  }, [
    benchmarkFlight?.outbound,
    benchmarkFlight?.returning,
    flightData.duration.days,
    flightData.duration.hours,
    flightData.duration.minutes,
    flightData.flightStops,
    flightData.segments,
    shopStep,
  ]);

  return (
    <FlightCard {...props}>
      {isBaseFlight && (
        <>
          <div className={cn("header")}>
            {t("core-packages.flights.includedInPackage")}
          </div>
          {props.index === 0 && (
            <div className={cn("footer")}>
              <p className={cn("footer-title")}>
                {t("core-packages.flights.additionalFlightsAvailableTitle")}
              </p>
              <p className={cn("footer-subtitle")}>
                {t("core-packages.flights.additionalFlightsAvailableSubtitle")}
              </p>
            </div>
          )}
        </>
      )}
    </FlightCard>
  );
};
